import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '@/components/ui/dialog.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Check } from 'lucide-react'
import { z } from 'zod'

import { cn } from '@/lib/utils.ts'
import { validate } from '@/repositories/userManagementRepository.ts'

type Props = {
  open: boolean
  invitedProject?: Project
  onSubmit: (username: string) => void
}

export const UserAgreementModal = ({ open, invitedProject, onSubmit }: Props) => {
  const [username, setUsername] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [agreeTerms, setAgreeTerms] = useState(false)

  const checklistLabels = [
    'Use all lowercase letters only',
    'No spaces allowed',
    'Must start with a letter or number',
    'Only letters and numbers are allowed (no special characters)',
    'No longer than 10 characters',
  ]

  const usernameSchema = z
    .string()
    .min(1, { message: 'required' })
    .max(10, { message: 'No longer than 10 characters' })
    .refine((val) => /^[^\s]+$/.test(val), { message: 'No spaces allowed' })
    .refine((val) => /^[a-z0-9]/.test(val), { message: 'Must start with a letter or number' })
    .refine((val) => /^[a-zA-Z0-9]+$/.test(val), {
      message: 'Only letters and numbers are allowed (no special characters)',
    })
    .refine((val) => !/[A-Z]/.test(val), { message: 'Use all lowercase letters only' })

  const result = usernameSchema.safeParse(username)
  const failedMessages = result.success ? [] : result.error.errors.map((e) => e.message)

  const checklist = checklistLabels.map((label) => ({
    label,
    valid: !failedMessages.includes(label),
  }))

  useEffect(() => {
    if (username.trim().length === 0) {
      setError(null)
      return
    }

    if (!result.success) {
      setError(null)
      return
    }

    const handler = setTimeout(() => {
      if (username.length <= 10) {
        validate(username).then((isValid) =>
          setError(isValid ? null : 'That username is currently in use. Please choose another name.'),
        )
      }
    }, 500)

    return () => clearTimeout(handler)
  }, [username, result])

  return (
    <Dialog open={open}>
      <DialogOverlay className="bg-surface-primary">
        <DialogContent className="[&>button:last-child]:hidden w-[512px] flex flex-col items-center gap-8 p-4 bg-surface-backgroundElevated rounded-2xl sm:rounded-2xl border-border-default-neutral">
          <DialogHeader className="pt-8">
            <DialogTitle>
              <div className="relative w-[175.82px] h-6 flex items-center">
                <img className="w-[26px] h-6" alt="Tracker Boot logo" src="/images/tb-logo.svg" />
                <img
                  className="w-[142px] h-[18px] ml-[7px]"
                  alt="Tracker Boot logo text"
                  src="/images/tb-logo-text.svg"
                />
              </div>
            </DialogTitle>
            <DialogDescription />
          </DialogHeader>
          <div className="flex flex-col gap-6">
            <span className="flex flex-col items-center w-full font-EN-KR-sub-heading-regular text-contents-primary">
              {invitedProject ? (
                <>
                  You've been invited to the project
                  <p className="font-EN-KR-sub-heading-bold text-contents-primary">{invitedProject?.name}</p>
                </>
              ) : (
                'Welcome aboard!!'
              )}
            </span>

            <div className="flex flex-col gap-6 px-6 w-full">
              <div className="flex flex-col gap-1 w-full">
                <Input
                  type="text"
                  placeholder="Create your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.trim())}
                  className={cn(
                    'bg-surface-backgroundElevated border-border-default-neutral rounded-xs',
                    error && 'border-border-releaseMarker-overdue focus:border-border-releaseMarker-overdue',
                  )}
                />
                {error && <p className="font-EN-KR-caption-regular text-contents-alert">{error}</p>}
                <ul className="flex flex-col gap-1 p-3 bg-tb-grey-100">
                  {checklist.map((item) => (
                    <li key={item.label} className="flex items-center gap-2 text-sm text-contents-tertiary">
                      {item.valid ? (
                        <Check className="text-tb-green-900 w-3.5 h-3.5" aria-label="checked" />
                      ) : (
                        <Check className="text-tb-grey-500 w-3.5 h-3.5 opacity-40" aria-label="unchecked" />
                      )}
                      <span className="font-JP-body2-regular text-contents-tertiary">{item.label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col gap-2 w-full">
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="terms-of-use"
                      variant="custom"
                      size="sm"
                      className="mt-1"
                      aria-label="Agree Terms of Use"
                      checked={agreeTerms}
                      onCheckedChange={(checked) => setAgreeTerms(checked as boolean)}
                    />

                    <Label htmlFor="terms-of-use" className="text-xs leading-4 cursor-pointer">
                      <span className="text-contents-tertiary">Yes, I have read and consent to the </span>
                      <a
                        href="/policies/terms-of-use"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-contents-accent font-bold hover:underline"
                      >
                        Terms of Use
                      </a>
                      <span className="text-contents-tertiary">, the </span>
                      <a
                        href="/policies/trackerboot-agreement"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-contents-accent font-bold hover:underline"
                      >
                        Tracker Boot Agreement
                      </a>
                      <span className="text-contents-tertiary">
                        {' '}
                        (Terms of Service), and the use of my data as described in the{' '}
                      </span>
                      <a
                        href="/policies/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-contents-accent font-bold hover:underline"
                      >
                        Privacy Policy
                      </a>
                      <span className="text-contents-tertiary">.</span>
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogClose asChild>
            <div className="w-full flex justify-end">
              <Button
                variant="primary"
                onClick={() => onSubmit(username)}
                disabled={error !== null || !agreeTerms || username.trim().length === 0 || !result.success}
                className="font-EN-KR-body1-bold"
              >
                Continue
              </Button>
            </div>
          </DialogClose>
        </DialogContent>
      </DialogOverlay>
    </Dialog>
  )
}
